import { BaseService } from '../base/BaseService';
import type { ServiceResponse } from '../base/ServiceTypes';

import type { Database } from '@/integrations/supabase/types';
import { logger } from '@/utils/logger';

type PostRow = Database['public']['Tables']['social_posts']['Row'];

export interface CalendarPostFilters {
  startDate: string;
  endDate: string;
  platforms?: string[];
  accountId?: string;
}

export class PostServiceEnhanced extends BaseService {
  private static instance: PostServiceEnhanced;

  private constructor() {
    super({
      defaultTTL: 2 * 60 * 1000,
      enableRetry: true,
      maxRetries: 3,
    });
  }

  public static getInstance(): PostServiceEnhanced {
    if (!PostServiceEnhanced.instance) {
      PostServiceEnhanced.instance = new PostServiceEnhanced();
    }
    return PostServiceEnhanced.instance;
  }


  async getCalendarPosts(filters: CalendarPostFilters): Promise<ServiceResponse<PostRow[]>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();

      if (import.meta.env.DEV) {
        logger.debug('📅 PostServiceEnhanced.getCalendarPosts - Fetching posts for calendar view');
        logger.debug('   Date range:', filters.startDate, 'to', filters.endDate);
      }

      let query = this.client
        .from('social_posts')
        .select('*')
        .or(
          `
          and(status.eq.scheduled,scheduled_at.gte.${filters.startDate},scheduled_at.lte.${filters.endDate}),
          and(status.eq.published,published_at.gte.${filters.startDate},published_at.lte.${filters.endDate}),
          and(status.eq.draft,scheduled_at.gte.${filters.startDate},scheduled_at.lte.${filters.endDate})
        `,
        );

      // Scope to the active workspace, keeping unattributed (legacy,
      // pre-workspace-stamping) rows visible — see resolveActiveBrandKitId in
      // PostService.ts for the full rationale.
      const activeKitId = await this.resolveActiveBrandKitId();
      if (activeKitId) {
        query = query.or(`brand_kit_id.eq.${activeKitId},brand_kit_id.is.null`);
      }

      const { data: allPosts, error } = await query.order('created_at', { ascending: false });

      if (error) {
        logger.error('❌ PostServiceEnhanced.getCalendarPosts error:', error);
        throw error;
      }

      let filteredPosts = allPosts || [];

      if (filters.platforms?.length) {
        filteredPosts = filteredPosts.filter((post) =>
          post.platforms?.some((p) => filters.platforms?.includes(p)),
        );
      }

      if (import.meta.env.DEV) {
        logger.debug(
          '✅ PostServiceEnhanced.getCalendarPosts found:',
          filteredPosts.length,
          'posts',
        );
        logger.debug('   By status:', {
          scheduled: filteredPosts.filter((p) => p.status === 'scheduled').length,
          published: filteredPosts.filter((p) => p.status === 'published').length,
          draft: filteredPosts.filter((p) => p.status === 'draft').length,
        });
      }

      return filteredPosts;
    }, 'Failed to get calendar posts');
  }

  async duplicatePost(postId: string): Promise<ServiceResponse<PostRow>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      if (import.meta.env.DEV)
        logger.debug('📋 PostServiceEnhanced.duplicatePost - Duplicating post:', postId);

      const { data: originalPost, error: fetchError } = await this.client
        .from('social_posts')
        .select('*')
        .eq('id', postId)
        .single();

      if (fetchError) {
        logger.error('❌ Error fetching original post:', fetchError);
        throw fetchError;
      }

      if (!originalPost) {
        throw new Error('Post not found');
      }

      // F-137: strip linkage + lifecycle fields so the copy is a fresh, UNBOUND
      // draft (same fix as PostService.duplicatePost — carrying plan_slot_id
      // split-brained the project slot). campaign_id kept on purpose.
      const {
        id: _id,
        created_at: _ca,
        updated_at: _ua,
        plan_slot_id: _ps,
        idempotency_key: _ik,
        approved_at: _aa,
        ...rest
      } = originalPost;
      const newPost = {
        ...rest,
        authored_by: userId, // The duplicating user is the author of the copy, not the original author
        status: 'draft' as const,
        scheduled_at: null,
        published_at: null,
        platform_post_id: null,
        platform_response: null,
        content: `${originalPost.content} (Copy)`,
      };

      const { data, error } = await this.client
        .from('social_posts')
        .insert(newPost)
        .select()
        .single();

      if (error) {
        logger.error('❌ Error duplicating post:', error);
        throw error;
      }

      if (import.meta.env.DEV) logger.debug('✅ Post duplicated successfully:', data.id);
      return data;
    }, 'Failed to duplicate post');
  }

  async deletePost(postId: string): Promise<ServiceResponse<void>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();

      if (import.meta.env.DEV)
        logger.debug('🗑️ PostServiceEnhanced.deletePost - Deleting post:', postId);

      const { error } = await this.client.from('social_posts').delete().eq('id', postId);

      if (error) {
        logger.error('❌ Error deleting post:', error);
        throw error;
      }

      if (import.meta.env.DEV) logger.debug('✅ Post deleted successfully');
    }, 'Failed to delete post');
  }
}

export const postServiceEnhanced = PostServiceEnhanced.getInstance();
